import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStore } from "zustand";
import { strapiStore } from "entities/strapi";
import { BioLoadingSpinner } from "widgets/loading";
import { LazyStories } from "utils/lazy-libs";
import Shadow from "../../../assets/Shadow.png";
import Close from "../../../assets/CloseStories.svg";
import AssetIcon from "../components/asset-icon";
import "./style.scss";

const CLOSE_DRAG_THRESHOLD = 70; // px
const SWIPE_HORIZONTAL_THRESHOLD = 60; // px
const GESTURE_INTENT_LOCK_THRESHOLD = 12; // px
const STORY_IMAGE_CACHE_LIMIT = 300;
const NEXT_ACTION_COOLDOWN_MS = 220;

// Persist loaded story images between modal remounts.
// This prevents loaders and repeated decode work for already-viewed urls.
const globalLoadedStoryBgUrls = new Set<string>();
const globalLoadedStoryBgImages = new Map<string, HTMLImageElement>();

const cacheStoryImage = (url: string, img: HTMLImageElement) => {
  if (!url) return;
  if (!globalLoadedStoryBgUrls.has(url)) {
    if (globalLoadedStoryBgUrls.size >= STORY_IMAGE_CACHE_LIMIT) {
      const oldestUrl = globalLoadedStoryBgUrls.values().next().value;
      if (oldestUrl) {
        globalLoadedStoryBgUrls.delete(oldestUrl);
        globalLoadedStoryBgImages.delete(oldestUrl);
      }
    }
    globalLoadedStoryBgUrls.add(url);
  }
  globalLoadedStoryBgImages.set(url, img);
};

type PropsType = {
  onAllStoriesEnd?: () => void;
  currentStoryIndex: number;
  onStoryIndexChange?: (newIndex: number) => void;
  whatToBuyStories?: boolean;
  data?: any;
};

type DragState = {
  isDragging: boolean;
  pointerId: number;
  startX: number;
  startY: number;
  startScrollLeft: number;
  deltaX: number;
  deltaY: number;
  intent: "horizontal" | "vertical" | null;
};

const StoriesModal: FC<PropsType> = ({
  onAllStoriesEnd,
  currentStoryIndex,
  onStoryIndexChange,
  whatToBuyStories,
  data,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("app");
  const { t: u } = useTranslation("union");
  const asset = data?.asset;
  const description = data?.info?.description || "";
  const color = data?.info?.color || "";
  const { stories } = useStore(strapiStore);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [brokenImage, setBrokenImage] = useState("");
  const [slideIndex, setSlideIndex] = useState(0);
  const [isStoryChanging, setIsStoryChanging] = useState<[boolean, string]>([
    false,
    "",
  ]);
  const storiesMainRef = useRef<HTMLDivElement | null>(null);
  const isClosingRef = useRef(false);
  const nextActionLockUntilRef = useRef(0);
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    deltaX: 0,
    deltaY: 0,
    intent: null,
  });

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    if (isClosingRef.current) return;

    // Only primary pointer to avoid multi-touch conflicts
    if (!e.isPrimary) return;

    dragStateRef.current.isDragging = true;
    dragStateRef.current.pointerId = e.pointerId;
    dragStateRef.current.startX = e.clientX;
    dragStateRef.current.startY = e.clientY;
    dragStateRef.current.startScrollLeft = containerRef.current.scrollLeft;
    dragStateRef.current.deltaX = 0;
    dragStateRef.current.deltaY = 0;
    dragStateRef.current.intent = null;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state.isDragging || !containerRef.current) return;
    if (e.pointerId !== state.pointerId) return;
    if (isClosingRef.current) return;

    // Prevent native scrolling while we drag
    e.preventDefault();

    const deltaX = e.clientX - state.startX;
    const deltaY = e.clientY - state.startY;

    // Lock intent after a small movement so vertical-close can't also swipe.
    if (!state.intent) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (
        absX >= GESTURE_INTENT_LOCK_THRESHOLD ||
        absY >= GESTURE_INTENT_LOCK_THRESHOLD
      ) {
        state.intent = absY > absX ? "vertical" : "horizontal";
      }
    }

    // Horizontal drag: slide the whole story container.
    if (state.intent === "horizontal") {
      containerRef.current.scrollLeft = state.startScrollLeft - deltaX;
    }

    state.deltaX = deltaX;
    state.deltaY = deltaY;
  };

  const finishPointer = () => {
    const state = dragStateRef.current;
    if (!state.isDragging) return;
    if (isClosingRef.current) {
      state.isDragging = false;
      state.pointerId = -1;
      state.deltaX = 0;
      state.deltaY = 0;
      state.intent = null;
      return;
    }

    const absX = Math.abs(state.deltaX);
    const absY = Math.abs(state.deltaY);

    state.isDragging = false;
    state.pointerId = -1;

    // 1) Predominantly vertical, downward drag → close stories
    if (
      onAllStoriesEnd &&
      state.intent === "vertical" &&
      state.deltaY > CLOSE_DRAG_THRESHOLD &&
      absY > absX
    ) {
      handleCloseStories();
    } else if (
      state.intent === "horizontal" &&
      absX > SWIPE_HORIZONTAL_THRESHOLD &&
      absX > absY
    ) {
      // 2) Predominantly horizontal swipe → reuse existing next/prev logic
      if (state.deltaX < 0) {
        if (!canRunNextAction()) {
          state.deltaX = 0;
          state.deltaY = 0;
          state.intent = null;
          return;
        }
        handleNextStory(true);
      } else {
        handlePrevStory();
      }
    }

    state.deltaX = 0;
    state.deltaY = 0;
    state.intent = null;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== dragStateRef.current.pointerId) return;
    finishPointer();
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== dragStateRef.current.pointerId) return;
    finishPointer();
  };

  const handleStoryChange = (newStoryIndex: number) => {
    if (isClosingRef.current) return;
    if (newStoryIndex !== currentStoryIndex) {
      setSlideIndex(0);
      onStoryIndexChange && onStoryIndexChange(newStoryIndex);
    }
  };

  const canRunNextAction = () => {
    const now = Date.now();
    if (now < nextActionLockUntilRef.current) return false;
    nextActionLockUntilRef.current = now + NEXT_ACTION_COOLDOWN_MS;
    return true;
  };

  const handleNextStory = (slideToNextStory: boolean = false) => {
    if (isClosingRef.current) return;
    if (!slideToNextStory) {
      if (slideIndex + 1 === stories[currentStoryIndex].items.length) {
        if (currentStoryIndex + 1 < stories.length) {
          setSlideIndex(0);
          handleStoryChange(currentStoryIndex + 1);
          setIsStoryChanging([true, "forward"]);
        } else {
          onAllStoriesEnd && handleCloseStories();
        }
      } else {
        setSlideIndex(slideIndex + 1);
      }
    } else {
      if (currentStoryIndex + 1 < stories.length) {
        setSlideIndex(0);
        handleStoryChange(currentStoryIndex + 1);
        setIsStoryChanging([true, "forward"]);
      } else {
        onAllStoriesEnd && handleCloseStories();
      }
    }
  };

  const handlePrevStory = () => {
    if (isClosingRef.current) return;
    if (slideIndex === 0 && currentStoryIndex > 0) {
      handleStoryChange(currentStoryIndex - 1);
      setIsStoryChanging([true, "backward"]);
      // setSlideIndex(stories[currentStoryIndex - 1].storyItems.length - 1);
    } else {
      setSlideIndex(slideIndex > 0 ? slideIndex - 1 : 0);
    }
  };

  // Helpers used only for callbacks coming from ReactInstaStories.
  // They defer state updates to avoid "setState during render" warnings.
  const scheduleNextFromStoriesLib = (slideToNextStory: false) => {
    if (whatToBuyStories) return;
    if (isClosingRef.current) return;
    if (!canRunNextAction()) return;
    setTimeout(() => {
      if (isClosingRef.current) return;
      handleNextStory(slideToNextStory);
    }, 0);
  };
  const schedulePrevFromStoriesLib = () => {
    if (isClosingRef.current) return;
    setTimeout(() => {
      if (isClosingRef.current) return;
      handlePrevStory();
    }, 0);
  };

  const handleCloseStories = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    storiesMainRef.current?.classList.add("is-stories-main-closing");
    containerRef.current?.classList.add("is-stories-container-closing");
    setTimeout(() => {
      onAllStoriesEnd && onAllStoriesEnd();
    }, 260);
  };

  // story change animate
  useEffect(() => {
    if (whatToBuyStories || !isStoryChanging[0]) return;

    const timeoutId = setTimeout(() => {
      setIsStoryChanging([false, ""]);
    }, 260);

    return () => clearTimeout(timeoutId);
  }, [isStoryChanging, whatToBuyStories]);

  // Ensure slideIndex is always within bounds of the current story's items.
  useEffect(() => {
    const storyGroup = stories?.[currentStoryIndex];
    const itemsLength = storyGroup?.items?.length ?? 0;

    if (itemsLength === 0) {
      if (slideIndex !== 0) {
        setSlideIndex(0);
      }
      return;
    }

    if (slideIndex > itemsLength - 1) {
      setSlideIndex(itemsLength - 1);
    }
  }, [stories, currentStoryIndex, slideIndex]);

  const currentStory = stories?.[currentStoryIndex];
  const currentStoryItems = currentStory?.items ?? [];
  const itemsLength = currentStoryItems.length;
  // Clamp before render so `react-insta-stories` never receives an out-of-range index during fast story group changes (swipe back/forward).
  const safeSlideIndex =
    itemsLength > 0 ? Math.min(Math.max(slideIndex, 0), itemsLength - 1) : 0;

  const storiesContent = useMemo(() => {
    if (!whatToBuyStories) {
      return currentStoryItems.map((story, i) => {
        return {
          content: () => (
            <div
              className="stories-main"
              data-direction={isStoryChanging[1]}
              // style={{
              //   backgroundImage: story.image ? `url(${story.image})` : "none",
              //   backgroundColor: story.image ? "none" : "var(--style1)",
              // }}
            >
              <img
                className="stories-image"
                src={story.image}
                alt=""
                onLoad={() => {
                  const url = story.image;
                  cacheStoryImage(url, new Image());
                }}
              />

              {!story.image && (
                <BioLoadingSpinner className="stories-image-loading-spinner" />
              )}

              <div
                className={
                  !story.title && !story.content
                    ? "stories-content-block-empty"
                    : "stories-content-block"
                }
              >
                {story.title && story.content && (
                  <div className="stories-content-block-slides-number">
                    {safeSlideIndex + 1}/{currentStoryItems.length}
                  </div>
                )}

                {story.title && <h4>{story.title}</h4>}

                {story.content && <p>{story.content}</p>}

                {story.action_btn && story.action_btn.link && (
                  <button
                    onClick={() => {
                      setTimeout(() => {
                        navigate(story.action_btn?.link!, {
                          state: story.action_btn?.params,
                        });
                      }, 150);
                    }}
                  >
                    {story.action_btn.text || u("heroButton")}
                  </button>
                )}
              </div>

              {story.disclaimer.link && (
                <a
                  href={story.disclaimer.link}
                  target="_blank"
                  rel="noreferrer"
                  className="stories-disclaimer-link"
                >
                  {story.disclaimer.text || t("disclaimer")}
                </a>
              )}
            </div>
          ),
        };
      });
    } else {
      return [
        {
          content: () => (
            <div className="stories-main asset-stories-main">
              <div
                className="asset-story-header"
                style={{
                  background: brokenImage
                    ? "linear-gradient(130.24deg, rgba(248, 211, 235, 0.7) -13.54%, rgba(187, 207, 250, 0.7) 113.9%)"
                    : "",
                }}
              >
                {(imageUrl || color) && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      ...(color
                        ? {
                            backgroundColor: color,
                            filter: "brightness(0.9)",
                          }
                        : {
                            backgroundImage: `url(${imageUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter:
                              "blur(22px) brightness(0.6) contrast(0.9) saturate(1.5)",
                            transform: "scale(8)",
                            backgroundRepeat: "no-repeat",
                          }),
                      objectFit: "cover",
                      zIndex: 5,
                    }}
                  />
                )}
                <div className="story-asset">
                  <AssetIcon
                    asset={asset}
                    onGetImageUrl={setImageUrl}
                    onGetBrokenImage={setBrokenImage}
                  />
                  <div className="story-asset-name">
                    <p>
                      {asset.symbol} | {asset.name}
                    </p>
                  </div>
                </div>
              </div>

              <span className="wtb-story-description">{description || ""}</span>
            </div>
          ),
        },
      ];
    }
  }, [
    whatToBuyStories,
    currentStoryItems,
    slideIndex,
    navigate,
    u,
    t,
    brokenImage,
    imageUrl,
    color,
    asset,
    description,
  ]);

  return (
    <div
      className="stories-container"
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div
        className="stories-container-outside"
        onClick={handleCloseStories}
      ></div>
      <div className="stories-main-wrapper" ref={storiesMainRef}>
        <img src={Shadow} alt="shadow" className="stories-shadow" />

        <img
          onClick={handleCloseStories}
          className="close-stories"
          src={Close}
          alt="close"
        />

        {whatToBuyStories || itemsLength > 0 ? (
          <LazyStories
            key={
              whatToBuyStories ? "wtb" : `${currentStoryIndex}-${itemsLength}`
            }
            stories={storiesContent}
            preventDefault={whatToBuyStories}
            currentIndex={safeSlideIndex}
            defaultInterval={whatToBuyStories ? 25000 : 15000}
            onNext={() => {
              scheduleNextFromStoriesLib(false);
            }}
            onStoryEnd={() => {
              scheduleNextFromStoriesLib(false);
            }}
            onPrevious={schedulePrevFromStoriesLib}
            preloadCount={1}
            width="100%"
            height="100%"
            keyboardNavigation
            progressContainerStyles={{
              zIndex: 990,
              width: "100%",
              paddingLeft: "24px",
              paddingRight: "24px",
            }}
            progressWrapperStyles={{
              width: "100%",
              marginTop: "52px",
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.5)",
            }}
            progressStyles={{
              width: "100%",
              background: !whatToBuyStories ? "var(--style2)" : "var(--style1)",
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(StoriesModal);

// const currentSlide = currentStoryItems?.[slideIndex];

// useEffect(() => {
//   const currentStoryId =
//     currentStory?.id ??
//     (typeof currentStory?.sort_id === "number" ? currentStory.sort_id : null);
//   const currentSlideId =
//     currentSlide?.id ??
//     (typeof currentSlide?.sort_id === "number" ? currentSlide.sort_id : null);
//   const currentStorySlidesIds = currentStoryItems.map((slide: any) => {
//     return (
//       slide?.id ?? (typeof slide?.sort_id === "number" ? slide.sort_id : null)
//     );
//   });

//   console.log("storyChange", {
//     storyIndex: currentStoryIndex,
//     storyId: currentStoryId,
//     slideIndex,
//     currentSlideId,
//     currentStorySlidesIds,
//   });
// }, [
//   currentStoryIndex,
//   slideIndex,
//   currentStory,
//   currentSlide,
//   currentStoryItems,
// ]);

// onAllStoriesEnd={() => {
//   console.log("onAllStoriesEnd");
//   if (whatToBuyStories) {
//     onAllStoriesEnd && onAllStoriesEnd();
//   } else {
//     scheduleNextFromStoriesLib(false);
//   }
// }}

// key={whatToBuyStories ? "wtb" : currentStoryIndex}
// onStoryStart={() => {
//   console.log(
//     "slide - ",
//     slideIndex,
//     "|   story - ",
//     currentStoryIndex,
//     "|   ====  ",
//     stories[currentStoryIndex].storyItems
//   );
// }}

// const bgUrl = getCdnUrl(
//   stories[currentStoryIndex].storyItems[slideIndex].content_image
//     ? getMLValue(
//         stories[currentStoryIndex].storyItems[slideIndex].content_images,
//         i18n.language,
//       )
//     : stories[currentStoryIndex].storyItems[slideIndex].content_image || "",
//   "story",
// );

// {story.use_content ? (
//   <div
//     className="story-text-content"
//     dangerouslySetInnerHTML={{
//       __html: getMLValue(
//         story.content,
//         i18n.language,
//       ),
//     }}
//   ></div>
// ) : (
//   <>
//     {/* <p>{getMLValue(story?.title, i18n.language)}</p> */}
//     <span>
//       {getMLValue(story.text, i18n.language)}
//     </span>
//   </>
// )}

// {getMLValue(story.link || {}, i18n.language) && (
//   <a
//     href={getMLValue(story.link, i18n.language)}
//     target="_blank"
//     rel="noreferrer"
//   >
//     {getMLValue(
//       story.link_text || {},
//       i18n.language,
//     ) || t("disclaimer")}
//   </a>
// )}

// <div className="text-stories-block">
// {/* //todo: header, content, pages 1/2, btn, only text, only btn, disclaimer */}

// {story.use_content ? (
//   <div
//     className="story-text-content"
//     dangerouslySetInnerHTML={{
//       __html: getMLValue(
//         story.content,
//         i18n.language,
//       ),
//     }}
//   ></div>
// ) : (
//   <>
//     {/* <p>{getMLValue(story?.title, i18n.language)}</p> */}
//     <span>
//       {getMLValue(story.text, i18n.language)}
//     </span>
//   </>
// )}

// {getMLValue(story.link || {}, i18n.language) && (
//   <a
//     href={getMLValue(story.link, i18n.language)}
//     target="_blank"
//     rel="noreferrer"
//   >
//     {getMLValue(
//       story.link_text || {},
//       i18n.language,
//     ) || t("disclaimer")}
//   </a>
// )}
// </div>
